export const MAX_BUG_REPORT_SCREENSHOTS = 3
export const MAX_BUG_REPORT_SCREENSHOT_BYTES = 5 * 1024 * 1024
export const BUG_REPORT_SCREENSHOT_ACCEPT = 'image/png,image/jpeg,image/gif'

export type ScreenshotErrorCode = 'type' | 'size' | 'read' | 'limit'
export type ScreenshotContentType = 'image/png' | 'image/jpeg' | 'image/gif'

export type BugReportScreenshot = {
  id: string
  filename: string
  contentType: ScreenshotContentType
  dataBase64: string
  previewUrl: string
}

export class BugReportScreenshotError extends Error {
  constructor(readonly code: ScreenshotErrorCode) {
    super(code)
  }
}

function isScreenshotContentType(
  value: string
): value is ScreenshotContentType {
  return (
    value === 'image/png' || value === 'image/jpeg' || value === 'image/gif'
  )
}

export async function prepareBugReportScreenshot(
  file: File
): Promise<BugReportScreenshot> {
  const contentType = file.type
  if (!isScreenshotContentType(contentType)) {
    throw new BugReportScreenshotError('type')
  }
  if (file.size === 0 || file.size > MAX_BUG_REPORT_SCREENSHOT_BYTES) {
    throw new BugReportScreenshotError('size')
  }
  const previewUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new BugReportScreenshotError('read'))
    }
    reader.onerror = () => reject(new BugReportScreenshotError('read'))
    reader.onabort = () => reject(new BugReportScreenshotError('read'))
    reader.readAsDataURL(file)
  })
  return {
    id: crypto.randomUUID(),
    filename: file.name.slice(0, 255),
    contentType,
    dataBase64: previewUrl.slice(previewUrl.indexOf(',') + 1),
    previewUrl,
  }
}

export function screenshotFilesFromClipboard(
  items: DataTransferItemList
): File[] {
  return Array.from(items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter(
      (file): file is File => file !== null && file.type.startsWith('image/')
    )
}

export function screenshotPayload(screenshot: BugReportScreenshot) {
  return {
    filename: screenshot.filename,
    content_type: screenshot.contentType,
    data_base64: screenshot.dataBase64,
  }
}
